import { applyMaskCnpj } from '../../../../../utils/utils';

/**
 * Regras compartilhadas da linha de instituição (custodiante / liquidante) do Cadastro Trademate.
 *
 * Moram fora dos componentes porque três lugares dependem de exatamente a mesma definição de
 * "linha preenchida": a validação da etapa 1, o descarte das linhas em branco antes do save e a
 * linha semeada por padrão. Se essas definições divergirem, o que o usuário digitou some sem
 * aviso — e não é erro que lint ou checagem de sintaxe pegam.
 *
 * `account_number` (conta B3) só é usado por custodiante; para liquidante o campo nem aparece na
 * tela e o backend grava null. Mesmo assim a linha dos dois tipos tem o campo, para o formato ser
 * único.
 */

/**
 * Mínimos da etapa 1. O texto vive aqui porque aparece em dois lugares — no toast que barra o
 * avanço (TrademateTab) e na mensagem de cada seção (TrademateStep1Data).
 *
 * O backend tem as mesmas mensagens em constantes próprias (trademate.controller.js): ele é a
 * autoridade e recusa `saveData` incompleto mesmo se a tela deixar passar.
 */
export const STEP1_REQUIRED_MESSAGES = {
    custodians: 'Informe ao menos um custodiante',
    settlements: 'Informe ao menos um liquidante',
    managers: 'Indique ao menos um gestor',

    //O Gestor de Recursos precisa estar entre os indicados: a Adesão o nomeia como Usuário
    //Privilegiado e a Ficha lista os indicados. Dois textos porque são dois problemas — ou o RPPS
    //não tem GR no cadastro (e não há quem selecionar), ou tem e ficou de fora.
    noResourceManager: 'Defina um único Gestor de Recursos no cadastro do RPPS',
    resourceManagerNotSelected: (nome) =>
        'Inclua o Gestor de Recursos (' + nome + ') entre os gestores indicados',
};

/**
 * Qual mensagem usar para a regra do Gestor de Recursos, ou null quando ela está satisfeita.
 *
 * Fica aqui porque tanto o toast que barra o avanço (TrademateTab) quanto a mensagem da seção
 * (TrademateStep1Data) precisam da mesma decisão.
 *
 * @param {{id: number, name: string, is_resource_manager: boolean}[]} availableManagers
 * @param {number[]} selectedIds
 * @returns {string|null}
 */
export const resourceManagerMessage = (availableManagers, selectedIds) => {

    const resourceManager = availableManagers.find(m => m.is_resource_manager);

    if (!resourceManager) return STEP1_REQUIRED_MESSAGES.noResourceManager;
    if (selectedIds.indexOf(resourceManager.id) === -1) {
        return STEP1_REQUIRED_MESSAGES.resourceManagerNotSelected(resourceManager.name);
    }

    return null;
};

export const emptyInstitution = (isMain) => ({
    name: '',
    cnpj: '',
    account_number: '',
    is_main: isMain,
});

/** Uma linha só é descartável quando NADA foi digitado nela — inclusive a conta B3. */
export const isFilledInstitution = (item) =>
    (item.name && item.name.trim() !== '')
    || (item.cnpj && item.cnpj.trim() !== '')
    || (item.account_number && item.account_number.trim() !== '');

/** Linha em branco é descartada no save, então não faz sentido acusá-la como inválida. */
export const isBlankInstitution = (item) => !isFilledInstitution(item);

const digits = (value) => String(value === null || value === undefined ? '' : value)
    .replace(/[^\d]+/g, '');

/**
 * Formata o CNPJ que vem do banco para exibição no input.
 *
 * É necessário porque o `MyTextField` só aplica a máscara no `onChange` — o `value` recebido é
 * renderizado como veio, e o backend grava só os 14 dígitos.
 *
 * A guarda não é preciosismo: o `applyMaskCnpj` devolve a string "CNPJ mal formado" quando não há
 * 14 dígitos e estoura em `null`, o que apareceria dentro do input nas linhas em branco.
 */
export const maskInstitutionCnpj = (value) => {
    const raw = value === null || value === undefined ? '' : String(value);
    return digits(raw).length === 14 && raw.length === 14 ? applyMaskCnpj(raw) : raw;
};

const text = (value) => String(value === null || value === undefined ? '' : value).trim();

const sameInstitutionList = (a, b) => {

    if (a.length !== b.length) return false;

    return a.every((item, i) => {
        const other = b[i];
        return text(item.name) === text(other.name)
            //O CNPJ na tela fica mascarado depois que o usuário digita; no `data` do backend vem
            //só com dígitos. Sem reduzir os dois lados, toda comparação daria "mudou".
            && digits(item.cnpj) === digits(other.cnpj)
            && text(item.account_number) === text(other.account_number)
            && (item.is_main === true) === (other.is_main === true);
    });
};

/**
 * Houve alteração na etapa 1 em relação ao que está salvo?
 *
 * Serve só para decidir se vale perguntar sobre invalidar documentos — quem decide de verdade o
 * que cai é o backend, que compara por documento. Aqui a resposta é por ficha inteira: apontar
 * exatamente quais documentos cairiam exigiria replicar o mapa de dependência no cliente.
 *
 * @param {{custodians: object[], settlements: object[], managers: object[]}} data o que está salvo
 * @param {object[]} cleanedCustodians já sem as linhas em branco
 * @param {object[]} cleanedSettlements já sem as linhas em branco
 * @param {number[]} managerIds na ordem de seleção
 */
export const hasStep1Changes = (data, cleanedCustodians, cleanedSettlements, managerIds) => {

    if (!data || !data.registration) return true;

    const savedManagerIds = data.managers.map(m => m.user_id).join(',');
    if (savedManagerIds !== managerIds.join(',')) return true;

    return !sameInstitutionList(data.custodians, cleanedCustodians)
        || !sameInstitutionList(data.settlements, cleanedSettlements);
};
